import {
  Flex,
  Box,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useColorModeValue,
  Button,
  ButtonGroup,
  useDisclosure,
  useToast,
} from '@chakra-ui/react'
import * as React from 'react'
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table'
import { AiOutlineDelete } from 'react-icons/ai'

// Custom components
import Card from 'components/card/Card'
import { SkeletonTable } from 'components/skeleton/Skeleton'
import { CustomPagination } from 'views/admin/communities/components/pagination'
import {
  useGetSubscribers,
  useDeleteSubscriber,
} from 'queries/subscriber.query'
import { DeleteSubscriberModal } from './DeleteSubscriberModal'

const columnHelper = createColumnHelper()

export default function SubscriberTable () {
  const [sorting, setSorting] = React.useState([])
  const textColor = useColorModeValue('secondaryGray.900', 'white')
  const TheadBackground = useColorModeValue('white', 'gray.800')
  const hoverBackgroundColor = useColorModeValue('gray.100', 'gray.700')
  const borderColor = useColorModeValue('gray.200', 'whiteAlpha.100')

  const { isOpen, onOpen, onClose } = useDisclosure() // For delete modal
  const [selectedId, setSelectedId] = React.useState(null)
  const toast = useToast()

  const { mutate: deleteMutate, isPending } = useDeleteSubscriber()

  const [pagination, setPagination] = React.useState({
    current: 1,
    pageSize: 10,
  })

  const { data, isLoading, isError, refetch } = useGetSubscribers(
    pagination.current,
    pagination.pageSize,
  )

  const rows = data?.data || []

  const handleSingleDelete = id => {
    setSelectedId(id)
    onOpen()
  }

  const onDelete = () => {
    if (!selectedId) return

    deleteMutate(selectedId, {
      onSuccess: () => {
        onClose()
        setSelectedId(null)
        refetch()
        toast({
          title: 'Subscriber Deleted.',
          status: 'success',
          duration: 3000,
          isClosable: true,
          position: 'top',
        })
      },
      onError: () => {
        toast({
          title: 'Deletion Failed.',
          status: 'error',
          duration: 5000,
          isClosable: true,
          position: 'top',
        })
      },
    })
  }

  // --- TABLE COLUMNS ---
  const columns = [
    columnHelper.accessor('email', {
      id: 'email',
      header: () => (
        <Text
          textAlign='center' // ADDED
          align='center'
          fontSize={{ sm: '10px', lg: '12px' }}
          color='gray.400'
        >
          Email Address
        </Text>
      ),
      cell: info => (
        <Flex justifyContent='center' alignItems='center'>
          <Text color={textColor} fontSize='sm' fontWeight='700'>
            {info.getValue()}
          </Text>
        </Flex>
      ),
    }),
    columnHelper.accessor('subscribed_at', {
      id: 'subscribed_at',
      header: () => (
        <Text
          textAlign='center' // ADDED
          align='center'
          fontSize={{ sm: '10px', lg: '12px' }}
          color='gray.400'
        >
          Subscription Date
        </Text>
      ),
      cell: info => (
        <Flex justifyContent='center' alignItems='center'>
          <Text color={textColor} fontSize='sm' textAlign='center'>
            {info.getValue()
              ? new Date(info.getValue()).toLocaleDateString()
              : 'N/A'}
          </Text>
        </Flex>
      ),
    }),
    columnHelper.accessor('actions', {
      id: 'actions',
      header: () => (
        <Text
          textAlign='center' // ADDED
          align='center'
          fontSize={{ sm: '10px', lg: '12px' }}
          color='gray.400'
        >
          Actions
        </Text>
      ),
      cell: ({ row }) => (
        <Flex justifyContent='center' alignItems='center'>
          <ButtonGroup size='sm' isAttached>
            <Button
              colorScheme='red'
              onClick={() => handleSingleDelete(row.original.id)}
              isLoading={isPending && selectedId === row.original.id}
            >
              <AiOutlineDelete />
            </Button>
          </ButtonGroup>
        </Flex>
      ),
      enableSorting: false,
    }),
  ]
  // --- END COLUMNS ---

  const table = useReactTable({
    data: rows,
    columns,
    state: { sorting },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  })

  if (isLoading) return <SkeletonTable />
  if (isError) return <Text color='red.500'>Error loading subscribers.</Text>

  return (
    <Card
      flexDirection='column'
      w='100%'
      px='0px'
      overflowX={{ sm: 'scroll', lg: 'hidden' }}
    >
      <Flex px='25px' mb='8px' justifyContent='space-between' align='center'>
        <Text
          color={textColor}
          fontSize='22px'
          fontWeight='700'
          lineHeight='100%'
        >
          Subscribers
        </Text>
      </Flex>
      <Box maxH='600px' overflowY='auto'>
        <Table color='gray.500' mb='24px' mt='12px'>
          <Thead position='sticky' zIndex='1' top='0' bg={TheadBackground}>
            {table.getHeaderGroups().map(headerGroup => (
              <Tr key={headerGroup.id}>
                {headerGroup.headers.map(header => (
                  <Th
                    key={header.id}
                    colSpan={header.colSpan}
                    borderColor={borderColor}
                    onClick={header.column.getToggleSortingHandler()} // Enables sorting
                    cursor={header.column.getCanSort() ? 'pointer' : 'default'} // Shows pointer if sortable
                    textAlign='center' // Center align headers
                  >
                    <Flex
                      justifyContent='center' // Center align content inside Th
                      align='center'
                      fontSize={{ sm: '10px', lg: '12px' }}
                      color='gray.400'
                    >
                      {/* Renders the column header name (e.g., 'Email Address') */}
                      {flexRender(
                        header.column.columnDef.header,
                        header.getContext(),
                      )}

                      {/* Renders sorting indicators */}
                      {{
                        asc: ' ↑',
                        desc: ' ↓',
                      }[header.column.getIsSorted()] ?? null}
                    </Flex>
                  </Th>
                ))}
              </Tr>
            ))}
          </Thead>

          <Tbody>
            {table.getRowModel().rows.map(row => (
              <Tr key={row.id} _hover={{ bg: hoverBackgroundColor }}>
                {row.getVisibleCells().map(cell => (
                  <Td key={cell.id}>
                    {/* Renders the cell content (e.g., the actual email address) */}
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </Td>
                ))}
              </Tr>
            ))}
          </Tbody>
        </Table>
      </Box>

      {/* PAGINATION BLOCK */}
      {data && data.total > 0 && (
        <CustomPagination
          total={data.total}
          current={pagination.current}
          pageSize={pagination.pageSize}
          onPageChange={page =>
            setPagination(prev => ({ ...prev, current: page }))
          }
          onPageSizeChange={size =>
            setPagination(prev => ({ ...prev, pageSize: size, current: 1 }))
          }
        />
      )}

      <DeleteSubscriberModal
        isOpen={isOpen}
        onClose={onClose}
        onDelete={onDelete}
        isLoading={isPending}
        subscriberId={selectedId}
      />
    </Card>
  )
}
